import { isServer } from "@/lib/constant";
import { Level, reportCustomMetric, reportError } from "@/lib/owl";

const KNB_SCRIPT_URL = "https://s0.meituan.net/bs/knb/v1.9.5/knb_titans.js";

let loadKNBPromise: Promise<void> | null = isServer
  ? Promise.resolve()
  : window?.FXBridge?.loadKNBPromise || null;

// 上报 KNB 初始化成功率
type LoadKNBErrMsg =
  | "success"
  | "openMiniProgram is not a function"
  | "knbReadyTimeout"
  | "loadKNBBridgeFail";
function reportLoadKNBSuccessRate(isSuccess: boolean, errMsg: LoadKNBErrMsg = "success") {
  reportCustomMetric({
    key: "loadKNB",
    value: isSuccess ? 1 : 0,
    tags: {
      errMsg,
    },
  });
}

/**
 * 加载 KNB 脚本，如果已执行过初始化，则直接返回初始化的 Promise
 * @returns Promise<void>
 */
function loadKNB(): Promise<void> {
  // SSR兼容性：服务端渲染时document不存在，返回rejected promise
  if (isServer) {
    return Promise.reject('KNB not available on server');
  }
  if (loadKNBPromise) return loadKNBPromise;
  loadKNBPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = KNB_SCRIPT_URL;
    script.onload = () => {
      resolve();
      (window.FXBridge = window.FXBridge || {}).isKNBLoadFinish = true;
      reportLoadKNBSuccessRate(true, "success");
    };
    script.onerror = () => {
      reject();
      (window.FXBridge = window.FXBridge || {}).isKNBLoadFinish = true;
      reportLoadKNBSuccessRate(false, "loadKNBBridgeFail");
    };
    document.head.appendChild(script);
  });

  return loadKNBPromise;
}

// 检查指定超时时间内 KNB 桥方法是否可执行
export function checkKNBBridgeEnable(bridgeAPI: string, timeout: number = 10000) {
  // SSR兼容性：服务端渲染时window不存在，返回false
  if (isServer) {
    return Promise.resolve(false);
  }
  let timer: number;
  const knbReadyPromise = new Promise((resolve) => {
    const check = () => {
      window?.KNB?.ready(() => {
        if (timer) {
          clearTimeout(timer);
        }
        if (typeof window?.KNB?.[bridgeAPI] === "function") {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    };

    if (window?.FXBridge?.isKNBLoadFinish) {
      // KNB 脚本已加载完成，直接检查桥方法是否可执行
      check();
    } else {
      // KNB 脚本未加载完成，先加载脚本，再检查桥方法是否可执行
      loadKNB().then(check).catch(() => resolve(false));
    }
  });

  const timeoutPromise = new Promise((resolve) => {
    timer = setTimeout(() => {
      // 超时，KNB 桥方法不可执行
      resolve(false);
    }, timeout) as unknown as number;
  });

  return Promise.race([knbReadyPromise, timeoutPromise]);
}

/**
 * 调用 KNB 桥方法
 * @param bridgeAPI KNB 桥方法名
 * @param props 方法参数
 * @returns Promise<any>
 */
export function callKNBBridge(bridgeAPI: string, props?: Record<string, any>) {
  // SSR兼容性：服务端渲染时window不存在，跳过调用
  if (isServer) return;

  checkKNBBridgeEnable(bridgeAPI)
    .then((enable) => {
      if (enable) {
        // KNB 桥初始化成功
        window.KNB[bridgeAPI](props);
      } else {
        // KNB 桥不支持 API 方法
        reportError({
          name: "[callKNBBridge] knbBridgeNotSupport",
          msg: `KNB 不支持 ${bridgeAPI} 方法`,
          category: "bridgeError",
          level: Level.ERROR,
        })
      }
    })
}

/**
 * 调用 KNB 桥方法
 * @param bridgeAPI KNB 桥方法名
 * @param props 方法参数
 * @returns Promise<any>
 */
export function callKNBUseBridge(bridgeAPI: string, props?: Record<string, any>) {
  // SSR兼容性：服务端渲染时window不存在，跳过调用
  if (isServer) return;

  checkKNBBridgeEnable('use')
    .then((enable) => {
      if (enable) {
        // KNB 桥初始化成功
        window.KNB.use(bridgeAPI, props);
      } else {
        // KNB 桥不支持 API 方法
        reportError({
          name: "[callKNBUseBridge] knbUseBridgeNotSupport",
          msg: `KNB 不支持 use 方法调用`,
          category: "bridgeError",
          level: Level.ERROR,
        })
      }
    })
}

/**
 * 初始化 KNB 桥
 */
export function initKNBBridge() {
  // SSR兼容性：服务端渲染时window不存在，跳过初始化
  if (isServer) return;

  if (window?.KNB) {
    (window.FXBridge = window.FXBridge || {}).isKNBLoadFinish = true;
    return;
  } else {
    loadKNB();
  }
}
